import React, { useEffect } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useSearchParam } from "react-use";
import PageHeading from "../../components/common/PageHeading";
import Tabs from "../../components/common/Tabs";
import SpringContainer from "../../components/containers/SpringContainer";
import EmptyDestinationsState from "../../components/features/EmptyDestinationsState";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import LoadingSpinner from "../../components/loaders/LoadingSpinner";
import Table from "../../components/table/Table";
import {
  ROUTE_DESTINATIONS,
  ROUTE_DESTINATIONS_EDIT,
} from "../../constants/routes";
import { DestinationTabs, DestinationTypeMap } from "../../enums/destination";
import { usePagination } from "../../hooks/usePagination";
import { Destination } from "../../models";
import ShopifyCard from "./components/cards/ShopifyCard";
import WordpressCard from "./components/cards/WordpressCard";
import { destinationColumns } from "./components/table/destinationColumns";
import useDestinations from "./hooks/useDestinations";

const tabs = [DestinationTabs.ALL, DestinationTabs.ADDED];

const Destinations = () => {
  const navigate = useNavigate();
  const tab = useSearchParam("tab");

  const {
    // Data
    destinations,
    totalCount,

    // Search
    searchInput,
    setSearchInput,
    searchQuery,
    setSearchQuery,

    // Pagination
    offset,
    setOffset,
    shouldShowPagination,
    limit,

    // Loading states
    isRefetching,
    isInitialLoading,

    // Actions
    refetch,
  } = useDestinations();

  const { paginationData } = usePagination({
    total: totalCount,
    offset,
    setOffset,
    limit,
    showPagination: shouldShowPagination,
  });

  useEffect(() => {
    if (!tab) {
      navigate(ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ADDED}`);
    }
  }, [tab, navigate]);

  const handleRowClick = (destination: Destination) => {
    navigate(
      generatePath(ROUTE_DESTINATIONS_EDIT, {
        id: destination?.destination_id!,
      }) + `?type=${DestinationTypeMap[destination?.destination_type_id]}`
    );
  };

  return (
    <SpringContainer>
      <div className="bg-white h-full">
        <div className="flex justify-between items-start lg:items-center md:mb-4">
          <div className="flex flex-col lg:flex-row gap-2 lg:items-center">
            <PageHeading
              heading="Destinations"
              tooltip="Refresh"
              loading={isRefetching}
              refetch={refetch}
            />
          </div>
        </div>

        {isInitialLoading ? (
          <div className="h-[50%] flex justify-center items-center">
            <LoadingSpinner size="32px" />
          </div>
        ) : (
          <>
            <div className="flex my-4">
              <Tabs
                tabs={tabs}
                selectedTab={tabs.find((t) => t === tab) ?? tabs[0]}
                onChange={(tab) => {
                  navigate(ROUTE_DESTINATIONS + `?tab=${tab.toLowerCase()}`);
                }}
              />
              {tab === DestinationTabs.ADDED &&
                (searchQuery || destinations.length > 0) && (
                  <div className="w-full flex items-center">
                    <Input
                      search
                      value={searchInput}
                      width="20rem"
                      className="ml-4"
                      placeholder="Search by title"
                      onChange={(e) => setSearchInput(e.target.value)}
                      onBlur={() => setSearchQuery(searchInput)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === "Tab") {
                          setSearchQuery(searchInput);
                        }
                      }}
                    />
                    <Button
                      variant="primary"
                      className="ml-auto"
                      onClick={() => {
                        navigate(
                          ROUTE_DESTINATIONS + `?tab=${DestinationTabs.ALL}`
                        );
                      }}
                    >
                      New
                    </Button>
                  </div>
                )}
            </div>

            {/* Show all destination cards */}
            {tab === DestinationTabs.ALL ? (
              <div className="flex gap-4 items-center">
                <WordpressCard />
                <ShopifyCard />
              </div>
            ) : searchQuery && destinations?.length === 0 ? (
              // Show no results found on search
              <div className="text-sm text-gray-500 my-4 mx-2">
                No destinations found
              </div>
            ) : destinations?.length === 0 ? (
              // Show empty state when no destinations are added
              <EmptyDestinationsState />
            ) : (
              // Show all destinations
              <Table<Destination>
                columns={destinationColumns}
                data={destinations}
                keyExtractor={(item) => item.destination_id!}
                paginationData={paginationData}
                onRowClick={handleRowClick}
              />
            )}
          </>
        )}
      </div>
    </SpringContainer>
  );
};

export default Destinations;
